import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TenantProvider } from './contexts/TenantContext';
import { useDynamicFaviconAndTitle } from './hooks/useDynamicFaviconAndTitle';
import useMarathiDisplayDigits from './hooks/useMarathiDisplayDigits';
import useMarathiInputTransliteration from './hooks/useMarathiInputTransliteration';
import InputLanguageSwitcher from './components/form/InputLanguageSwitcher';
import MarathiCandidateOverlay from './components/form/MarathiCandidateOverlay';
import { hasPermission } from './rbac';
import GlobalTopLoader from './components/common/GlobalTopLoader';
import LoadingSpinner from './components/common/LoadingSpinner';

import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import ProfilePage from './pages/ProfilePage';

import PublicLayout from './components/public/PublicLayout';
import HomePage from './pages/public/HomePage';
import AboutPage from './pages/public/AboutPage';
import ServicesPage from './pages/public/ServicesPage';
import SchemesPage from './pages/public/SchemesPage';
import GramsabhaPage from './pages/public/GramsabhaPage';
import ComplaintsPage from './pages/public/ComplaintsPage';
import NoticesPage from './pages/public/NoticesPage';
import GalleryPage from './pages/public/GalleryPage';
import ContactPage from './pages/public/ContactPage';
import ProjectsPage from './pages/public/ProjectsPage';
import ProjectDetailPage from './pages/public/ProjectDetailPage';
import DepartmentsPage from './pages/public/DepartmentsPage';
import DepartmentDetailPage from './pages/public/DepartmentDetailPage';
import SchemeDetailPage from './pages/public/SchemeDetailPage';
import RegisterPage from './pages/public/RegisterPage';
import Facilities from './pages/Facilities';
import Businesses from './pages/Businesses';
import StaffDirectory from './pages/StaffDirectory';
import CustomPageView from './pages/public/CustomPageView';
import CitizenDashboardPage from './pages/public/CitizenDashboardPage';
import CitizenApplyPage from './pages/public/CitizenApplyPage';
import CertificateViewPage from './pages/public/CertificateViewPage';
import VerifyCertificatePage from './pages/public/VerifyCertificatePage';
import BusinessWorkspacePage from './pages/public/BusinessWorkspacePage';
import BusinessComplianceWorkspacePage from './pages/public/BusinessComplianceWorkspacePage';
import TaxAccountWorkspacePage from './pages/public/TaxAccountWorkspacePage';
import LegalPage from './pages/public/LegalPage';

import AdminLayout from './components/admin/AdminLayout';
import Dashboard from './pages/admin/Dashboard';
import Households from './pages/admin/Households';
import Citizens from './pages/admin/Citizens';
import Certificates from './pages/admin/Certificates';
import AdminServicesCertificates from './pages/admin/AdminServicesCertificates';
import AdminComplaints from './pages/admin/AdminComplaints';
import AdminComplaintDetail from './pages/admin/AdminComplaintDetail';
import Schemes from './pages/admin/Schemes';
import Gramsabha from './pages/admin/Gramsabha';
import Finance from './pages/admin/Finance';
import Projects from './pages/admin/Projects';
import Content from './pages/admin/Content';
import AdminNotices from './pages/admin/AdminNotices';
import AdminContactMessages from './pages/admin/AdminContactMessages';
import AdminTaxWorkspace from './pages/admin/AdminTaxWorkspace';
import AdminPropertiesList from './pages/admin/AdminPropertiesList';
import AdminPropertyForm from './pages/admin/AdminPropertyForm';
import AdminPropertyWorkspace from './pages/admin/AdminPropertyWorkspace';
import AdminRegistryReviewQueue from './pages/admin/AdminRegistryReviewQueue';
import { canOpenRegistryReviewQueue } from './pages/admin/registryReviewQueueUi';
import AdminBusinessCompliance from './pages/admin/AdminBusinessCompliance';
import AdminAuditLog from './pages/admin/AdminAuditLog';

// New Admin Form Pages
import AdminFacilitiesList from './pages/admin/AdminFacilitiesList';
import AdminFacilityForm from './pages/admin/AdminFacilityForm';
import AdminBusinessesList from './pages/admin/AdminBusinessesList';
import AdminBusinessForm from './pages/admin/AdminBusinessForm';
import AdminDepartmentList from './pages/admin/AdminDepartmentList';
import AdminDepartmentForm from './pages/admin/AdminDepartmentForm';
import AdminStaffList from './pages/admin/AdminStaffList';
import AdminStaffMemberForm from './pages/admin/AdminStaffMemberForm';
import AdminUserApprovals from './pages/admin/AdminUserApprovals';
import AdminDocumentation from './pages/admin/AdminDocumentation';

// Taluka Officer Portal
import TalukaLayout from './components/taluka/TalukaLayout';
import TalukaDashboard from './pages/taluka/TalukaDashboard';
import TalukaGPs from './pages/taluka/TalukaGPs';
import TalukaComplaints from './pages/taluka/TalukaComplaints';
import TalukaCitizens from './pages/taluka/TalukaCitizens';
import TalukaPopulation from './pages/taluka/TalukaPopulation';
import TalukaStaff from './pages/taluka/TalukaStaff';
import TalukaNotices from './pages/taluka/TalukaNotices';
import TalukaFacilities from './pages/taluka/TalukaFacilities';
import TalukaBusinesses from './pages/taluka/TalukaBusinesses';

// System Admin Portal
import SystemLayout from './components/system/SystemLayout';
import SystemDashboard from './pages/system/SystemDashboard';
import SystemTenants from './pages/system/SystemTenants';
import SystemUsers from './pages/system/SystemUsers';
import SystemRoles from './pages/system/SystemRoles';
import SystemLegacyTaxMigration from './pages/system/SystemLegacyTaxMigration';

function ProtectedRoute({ children, loginPath = '/login' }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <LoadingSpinner fullPage message="प्रणाली लोड होत आहे..." />;
  if (!isAuthenticated) return <Navigate to={loginPath} />;
  return children;
}

function TalukaOfficerRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner fullPage message="प्रणाली लोड होत आहे..." />;
  if (!user || !['tahashil_officer', 'sdo_officer'].includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function SystemAdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner fullPage message="प्रणाली लोड होत आहे..." />;
  if (!user || user.role !== 'super_admin') return <Navigate to="/" replace />;
  return children;
}

function PermissionRoute({ module, action = 'view', children }) {
  const { user, loading } = useAuth();
  const { tenantSlug } = useParams();
  if (loading) return <LoadingSpinner fullPage message="परवानग्या तपासल्या जात आहेत..." />;
  if (!hasPermission(user, module, action)) {
    return <Navigate to={`/admin/${tenantSlug}/dashboard`} replace />;
  }
  return children;
}

function RegistryReviewPermissionRoute({ children }) {
  const { user, loading } = useAuth();
  const { tenantSlug } = useParams();
  if (loading) return <LoadingSpinner fullPage message="पडताळणी लोड होत आहे..." />;
  if (!canOpenRegistryReviewQueue(user)) {
    return <Navigate to={`/admin/${tenantSlug}/dashboard`} replace />;
  }
  return children;
}

function DynamicMetadata() {
  useDynamicFaviconAndTitle();
  return null;
}

function App() {
  useMarathiDisplayDigits();
  useMarathiInputTransliteration();

  return (
    <BrowserRouter>
      <AuthProvider>
        <TenantProvider>
          <GlobalTopLoader />
          <DynamicMetadata />
          <InputLanguageSwitcher />
          <MarathiCandidateOverlay />
          <Routes>
            {/* Landing */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage portal="gp" />} />
            <Route path="/taluka/login" element={<LoginPage portal="taluka" />} />
            <Route path="/system/login" element={<LoginPage portal="system" />} />
            <Route path="/legal/:policy" element={<LegalPage />} />

            {/* Public GP Portal */}
            <Route path="/gp/:tenantSlug" element={<PublicLayout />}>
              <Route index element={<HomePage />} />
              <Route path="about" element={<AboutPage />} />
              <Route path="services" element={<ServicesPage />} />
              <Route path="taxes" element={<Navigate to="../my-taxes" replace />} />
              <Route path="my-taxes" element={<ProtectedRoute><TaxAccountWorkspacePage /></ProtectedRoute>} />
              <Route path="schemes" element={<SchemesPage />} />
              <Route path="gramsabha" element={<GramsabhaPage />} />
              <Route path="gramsabha/:meetingId" element={<GramsabhaPage />} />
              <Route path="projects" element={<ProjectsPage />} />
              <Route path="projects/:projectId" element={<ProjectDetailPage />} />
              <Route path="departments" element={<DepartmentsPage />} />
              <Route path="departments/:departmentId" element={<DepartmentDetailPage />} />
              <Route path="schemes/:schemeId" element={<SchemeDetailPage />} />
              <Route path="complaints" element={<ComplaintsPage />} />
              <Route path="notices" element={<NoticesPage />} />
              <Route path="gallery" element={<GalleryPage />} />
              <Route path="contact" element={<ContactPage />} />
              <Route path="register" element={<RegisterPage />} />
              <Route path="facilities" element={<Facilities />} />
              <Route path="businesses" element={<Businesses />} />
              <Route path="my-businesses" element={<BusinessWorkspacePage />} />
              <Route path="my-businesses/compliance" element={<BusinessComplianceWorkspacePage />} />
              <Route path="staff" element={<StaffDirectory />} />
              <Route path="page/:pageSlug" element={<CustomPageView />} />
              <Route path="dashboard" element={<CitizenDashboardPage />} />
              <Route path="apply-certificate" element={<CitizenApplyPage />} />
              <Route path="certificate/:certId/view" element={<CertificateViewPage />} />
              <Route path="verify/:certificateNumber" element={<VerifyCertificatePage />} />
              <Route path="verify" element={<VerifyCertificatePage />} />
              <Route path="legal/:policy" element={<LegalPage />} />
            </Route>

            {/* Admin Panel */}
            <Route path="/admin/:tenantSlug" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="households" element={<Households />} />
              <Route path="citizens" element={<Citizens />} />
              <Route path="certificates" element={<Certificates />} />
              <Route path="services-certificates" element={<AdminServicesCertificates />} />
              <Route path="complaints" element={<AdminComplaints />} />
              <Route path="complaints/:complaintId" element={<AdminComplaintDetail />} />
              <Route path="schemes" element={<Schemes />} />
              <Route path="gramsabha" element={<Gramsabha />} />
              <Route path="finance" element={<Finance />} />
              <Route path="taxes" element={<Navigate to="../tax-workspace" replace />} />
              <Route path="tax-workspace" element={<AdminTaxWorkspace />} />
              <Route path="registry-change-requests" element={<RegistryReviewPermissionRoute><AdminRegistryReviewQueue /></RegistryReviewPermissionRoute>} />
              <Route path="properties" element={<AdminPropertiesList />} />
              <Route path="properties/new" element={<AdminPropertyForm />} />
              <Route path="properties/:propertyUid/edit" element={<AdminPropertyForm />} />
              <Route path="properties/:propertyUid" element={<AdminPropertyWorkspace />} />
              <Route path="business-compliance" element={<AdminBusinessCompliance />} />
              <Route path="audit-log" element={<PermissionRoute module="audit_log"><AdminAuditLog /></PermissionRoute>} />
              <Route path="projects" element={<Projects />} />
              <Route path="content" element={<Content />} />
              <Route path="notices" element={<AdminNotices />} />
              <Route path="contact-messages" element={<AdminContactMessages />} />

              {/* Facilities Routes */}
              <Route path="facilities" element={<AdminFacilitiesList />} />
              <Route path="facilities/new" element={<AdminFacilityForm />} />
              <Route path="facilities/:facilityId" element={<AdminFacilityForm />} />

              {/* Businesses Routes */}
              <Route path="businesses" element={<AdminBusinessesList />} />
              <Route path="businesses/new" element={<AdminBusinessForm />} />
              <Route path="businesses/:businessId" element={<AdminBusinessForm />} />

              {/* Departments Routes */}
              <Route path="departments" element={<AdminDepartmentList />} />
              <Route path="departments/new" element={<AdminDepartmentForm />} />
              <Route path="departments/:departmentId" element={<AdminDepartmentForm />} />

              {/* Staff Routes */}
              <Route path="staff" element={<AdminStaffList />} />
              <Route path="staff/new" element={<AdminStaffMemberForm />} />
              <Route path="staff/:staffId" element={<AdminStaffMemberForm />} />
              <Route path="user-approvals" element={<AdminUserApprovals />} />
              <Route path="documentation" element={<AdminDocumentation />} />
              <Route path="profile" element={<ProfilePage />} />
            </Route>

            {/* Taluka Officer Portal */}
            <Route path="/taluka" element={<ProtectedRoute loginPath="/taluka/login"><TalukaOfficerRoute><TalukaLayout /></TalukaOfficerRoute></ProtectedRoute>}>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<TalukaDashboard />} />
              <Route path="gps" element={<TalukaGPs />} />
              <Route path="complaints" element={<TalukaComplaints />} />
              <Route path="citizens" element={<TalukaCitizens />} />
              <Route path="population" element={<TalukaPopulation />} />
              <Route path="staff" element={<TalukaStaff />} />
              <Route path="notices" element={<TalukaNotices />} />
              <Route path="facilities" element={<TalukaFacilities />} />
              <Route path="businesses" element={<TalukaBusinesses />} />
            </Route>

            {/* System Admin Portal */}
            <Route path="/system" element={<ProtectedRoute loginPath="/system/login"><SystemAdminRoute><SystemLayout /></SystemAdminRoute></ProtectedRoute>}>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<SystemDashboard />} />
              <Route path="tenants" element={<SystemTenants />} />
              <Route path="users" element={<SystemUsers />} />
              <Route path="roles" element={<SystemRoles />} />
              <Route path="legacy-tax-migration" element={<SystemLegacyTaxMigration />} />
            </Route>

            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </TenantProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
